import { useState } from 'react';
import { ApiError, api } from '../api';
import { Banner, Field, JsonEditor, Pill, Spinner, useAsync } from '../components/Common';
import { DataTable } from '../components/Blocks';
import { SyncAll } from '../components/SyncAll';
import { formatDateTime, relativeTime } from '../format';

interface DatasetStats {
  rows: number;
  snapshots: number;
  oldest: string | null;
  newest: string | null;
  bytes: number;
}

interface Dataset {
  id: number;
  slug: string;
  name: string;
  description: string | null;
  request_slug: string;
  params: Record<string, any>;
  mode: 'snapshot' | 'upsert' | 'append';
  key_columns: string[];
  cron: string | null;
  timezone: string;
  enabled: boolean;
  retention_days: number | null;
  last_sync_at: string | null;
  last_status: string | null;
  next_sync_at: string | null;
  stats: DatasetStats;
}

const MODE_HELP: Record<string, string> = {
  snapshot:
    'Keeps a dated copy every sync. Use for anything whose value at a point in time matters — open vacancies, pipeline, ageing. This is what makes "as at last Tuesday" answerable.',
  upsert: 'Keeps one current row per key. Use for reference data with no meaningful history — the consultant list, job titles.',
  append: 'Keeps every row ever seen, matched by key. Use for data already dated at source — weekly activity, calls, placements.',
};

function humanBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

/**
 * The local store.
 *
 * This screen exists so the question "how current is this, and how far back does
 * it go?" has an answer a manager can find without asking anyone.
 */
export function Datasets({ slug, canEdit, onNavigate }: { slug?: string; canEdit: boolean; onNavigate: (path: string) => void }) {
  const datasets = useAsync(() => api.get<Dataset[]>('/api/datasets'));
  const [creating, setCreating] = useState(false);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [message, setMessage] = useState<{ tone: 'good' | 'error'; text: string } | null>(null);

  const syncNow = async (dataset: Dataset) => {
    setBusyId(dataset.id);
    setMessage(null);
    try {
      const r = await api.post<any>(`/api/datasets/${dataset.id}/sync`);
      setMessage({
        tone: r.status === 'ok' ? 'good' : 'error',
        text:
          r.status === 'ok'
            ? `“${dataset.name}”: ${r.rowCount} row(s) fetched — ${r.inserted} new, ${r.updated} already held${r.pruned ? `, ${r.pruned} pruned` : ''}.${r.warnings?.length ? ` ${r.warnings.join(' ')}` : ''}`
            : `“${dataset.name}” failed: ${r.error}`,
      });
      datasets.reload();
    } catch (err) {
      setMessage({ tone: 'error', text: err instanceof ApiError ? err.message : String(err) });
    } finally {
      setBusyId(null);
    }
  };

  if (slug) return <DatasetDetail slug={slug} canEdit={canEdit} onBack={() => onNavigate('datasets')} />;
  if (datasets.loading && !datasets.data) return <Spinner label="Loading the store…" />;
  if (datasets.error) return <Banner tone="error">{datasets.error}</Banner>;

  const list = datasets.data ?? [];

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Stored data</h1>
          <p>
            Data pulled from the source systems on a schedule and kept here. Reports read the store instead of querying
            live — which is faster, survives an API being down, and is the only way to answer questions about weeks that
            have already passed.
          </p>
        </div>
        {canEdit && (
          <div className="page-actions">
            <button className="primary" onClick={() => setCreating(true)}>
              New dataset
            </button>
          </div>
        )}
      </div>

      {message && <Banner tone={message.tone}>{message.text}</Banner>}

      {canEdit && list.some((d) => d.enabled) && <SyncAll onFinished={() => datasets.reload?.()} />}

      {!list.length && !creating && (
        <Banner tone="info" title="Nothing stored yet. ">
          A dataset is a request that runs on a schedule and keeps its results. Until one exists, every report queries the
          source systems live — fine for one manager, slow and rate-limited for a team.
        </Banner>
      )}

      {creating && (
        <DatasetEditor
          dataset={null}
          onClose={() => setCreating(false)}
          onSaved={() => {
            setCreating(false);
            datasets.reload();
          }}
        />
      )}

      <div className="grid-cards">
        {list.map((d) => (
          <div className="card" key={d.id} style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <div>
              <h3 style={{ margin: 0 }}>
                {d.name} {d.enabled ? null : <Pill tone="off">paused</Pill>}
              </h3>
              <div className="mono" style={{ fontSize: 11, color: 'var(--muted)' }}>
                {d.slug} · {d.mode}
              </div>
            </div>
            {d.description && <p style={{ margin: 0, fontSize: 12.5, color: 'var(--ink-2)' }}>{d.description}</p>}

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, fontSize: 12, color: 'var(--ink-2)' }}>
              <span>
                <strong>{d.stats.rows.toLocaleString('en-GB')}</strong> rows
              </span>
              <span>{humanBytes(d.stats.bytes)}</span>
              <span>
                {d.stats.snapshots} snapshot{d.stats.snapshots === 1 ? '' : 's'}
              </span>
              <span>{d.stats.oldest ? `from ${d.stats.oldest}` : 'no data yet'}</span>
            </div>

            <div style={{ fontSize: 12, color: 'var(--muted)' }}>
              Last sync {relativeTime(d.last_sync_at)}
              {d.last_status ? (
                d.last_status.startsWith('ok') ? (
                  <>
                    {' '}
                    <Pill tone="ok">ok</Pill>
                  </>
                ) : (
                  <>
                    {' '}
                    <Pill tone="bad">failed</Pill>
                  </>
                )
              ) : null}
              {d.cron ? <> · next {d.enabled ? formatDateTime(d.next_sync_at) : 'paused'}</> : <> · no schedule</>}
            </div>

            {d.last_status && !d.last_status.startsWith('ok') && (
              <p style={{ margin: 0, fontSize: 11.5, color: 'var(--critical)' }}>{d.last_status}</p>
            )}

            <div style={{ display: 'flex', gap: 6, marginTop: 'auto', paddingTop: 6 }}>
              <button className="small" onClick={() => onNavigate(`datasets/${d.slug}`)}>
                Open
              </button>
              {canEdit && (
                <button className="small" onClick={() => syncNow(d)} disabled={busyId === d.id}>
                  {busyId === d.id ? 'Syncing…' : 'Sync now'}
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    </>
  );
}

function DatasetDetail({ slug, canEdit, onBack }: { slug: string; canEdit: boolean; onBack: () => void }) {
  const detail = useAsync(() => api.get<any>(`/api/datasets/${slug}`), [slug]);
  const [editing, setEditing] = useState(false);
  const [range, setRange] = useState<{ from: string; to: string } | null>(null);
  const preview = useAsync(
    () =>
      api.get<any>(
        `/api/datasets/${slug}/rows?limit=200&${range ? `from=${range.from}&to=${range.to}` : 'latest=false'}`,
      ),
    [slug, range?.from, range?.to],
  );

  if (detail.loading && !detail.data) return <Spinner label="Loading dataset…" />;
  if (detail.error) return <Banner tone="error">{detail.error}</Banner>;
  const d = detail.data;
  if (!d) return null;

  // Lead with when the row was written and with the key columns. On a history
  // dataset the row's own date is the whole point, and column order coming
  // straight from the source usually buries it off the right-hand edge.
  const rawColumns: string[] = preview.data?.rows?.length ? Object.keys(preview.data.rows[0]) : (d.columns ?? []);
  const leading = ['snapshot_at', ...(d.key_columns ?? [])].filter((c: string) => rawColumns.includes(c));
  const ordered = [...leading, ...rawColumns.filter((c: string) => !leading.includes(c))];
  const columns = ordered.map((key: string) => ({ key, label: key, format: 'text' as const }));

  return (
    <>
      <div className="page-head">
        <div>
          <h1>{d.name}</h1>
          <p>{d.description}</p>
        </div>
        <div className="page-actions">
          <button onClick={onBack}>Back</button>
          {canEdit && <button onClick={() => setEditing((v) => !v)}>{editing ? 'Close editor' : 'Edit'}</button>}
        </div>
      </div>

      <div className="kpi-row" style={{ marginBottom: 18 }}>
        <div className="kpi">
          <div className="label">Rows held</div>
          <div className="value">{d.stats.rows.toLocaleString('en-GB')}</div>
        </div>
        <div className="kpi">
          <div className="label">Snapshots</div>
          <div className="value">{d.stats.snapshots}</div>
          <div className="sub">{d.mode} mode</div>
        </div>
        <div className="kpi">
          <div className="label">Written between</div>
          <div className="value" style={{ fontSize: 15 }}>
            {d.stats.oldest ?? '—'} → {d.stats.newest ?? '—'}
          </div>
        </div>
        <div className="kpi">
          <div className="label">Size on disk</div>
          <div className="value" style={{ fontSize: 20 }}>
            {humanBytes(d.stats.bytes)}
          </div>
        </div>
      </div>

      {d.mode !== 'snapshot' && (
        <Banner tone="info">
          In <strong>{d.mode}</strong> mode the stored dates are when rows were <em>written down</em>, not the period they
          describe — a backfill run today stores months of history all stamped today. Filter on the rows’ own date column
          for the period.
        </Banner>
      )}

      {editing && (
        <DatasetEditor
          dataset={d}
          onClose={() => setEditing(false)}
          onSaved={() => {
            setEditing(false);
            detail.reload();
          }}
        />
      )}

      <div className="card" style={{ marginBottom: 16 }}>
        <h3>Stored rows</h3>
        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', marginBottom: 12, flexWrap: 'wrap' }}>
          <Field label="Written from">
            <input type="date" value={range?.from ?? ''} onChange={(e) => setRange({ from: e.target.value, to: range?.to ?? e.target.value })} />
          </Field>
          <Field label="Written to">
            <input type="date" value={range?.to ?? ''} onChange={(e) => setRange({ from: range?.from ?? e.target.value, to: e.target.value })} />
          </Field>
          <button style={{ marginBottom: 12 }} onClick={() => setRange(null)}>
            All
          </button>
        </div>
        {preview.data?.warnings?.length ? <Banner tone="warn" items={preview.data.warnings} /> : null}
        {preview.loading && !preview.data ? (
          <Spinner label="Reading…" />
        ) : (
          <>
            <DataTable rows={preview.data?.rows ?? []} columns={columns} pageSize={25} />
            {preview.data?.truncated && <p className="note">Showing the first 200 of {preview.data.rowCount} rows.</p>}
          </>
        )}
      </div>

      <div className="card">
        <h3>Sync history</h3>
        <div className="table-wrap">
          <table className="grid">
            <thead>
              <tr>
                <th>Started</th>
                <th>Trigger</th>
                <th className="right">Fetched</th>
                <th className="right">New</th>
                <th className="right">Existing</th>
                <th className="right">Pruned</th>
                <th className="right">Time</th>
                <th>Result</th>
              </tr>
            </thead>
            <tbody>
              {(d.recentSyncs ?? []).map((s: any) => (
                <tr key={s.id}>
                  <td>{formatDateTime(s.started_at)}</td>
                  <td>{s.trigger}</td>
                  <td className="right">{s.row_count}</td>
                  <td className="right">{s.inserted}</td>
                  <td className="right">{s.updated}</td>
                  <td className="right">{s.pruned}</td>
                  <td className="right">{s.duration_ms ? `${(s.duration_ms / 1000).toFixed(1)}s` : '—'}</td>
                  <td>
                    {s.status === 'ok' ? <Pill tone="ok">ok</Pill> : <Pill tone="bad">{s.error ?? s.status}</Pill>}
                    {s.warnings?.length ? <span style={{ marginLeft: 6, fontSize: 11, color: 'var(--muted)' }}>{s.warnings.length} warning(s)</span> : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {!(d.recentSyncs ?? []).length && <p className="note">No syncs recorded yet.</p>}
      </div>
    </>
  );
}

function DatasetEditor({ dataset, onClose, onSaved }: { dataset: any | null; onClose: () => void; onSaved: () => void }) {
  const requests = useAsync(() => api.get<{ slug: string; name: string }[]>('/api/requests'));
  const [form, setForm] = useState({
    name: dataset?.name ?? '',
    description: dataset?.description ?? '',
    request_slug: dataset?.request_slug ?? '',
    mode: dataset?.mode ?? 'snapshot',
    cron: dataset?.cron ?? '0 6 * * *',
    timezone: dataset?.timezone ?? 'Europe/London',
    enabled: dataset?.enabled ?? true,
    retention_days: dataset?.retention_days ?? '',
  });
  const [params, setParams] = useState<Record<string, any>>(dataset?.params ?? {});
  const [keyColumns, setKeyColumns] = useState<string[]>(dataset?.key_columns ?? []);
  const [valid, setValid] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const save = async (confirmReset = false) => {
    if (!valid) {
      setError('Fix the invalid JSON before saving.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const payload: any = {
        ...form,
        params,
        key_columns: keyColumns,
        retention_days: form.retention_days === '' ? null : Number(form.retention_days),
        confirmReset,
      };
      if (dataset) await api.put(`/api/datasets/${dataset.id}`, payload);
      else await api.post('/api/datasets', payload);
      onSaved();
    } catch (err) {
      const message = err instanceof ApiError ? err.message : String(err);
      setError(message);
      // The server refuses a mode change that would orphan stored rows; offer
      // the explicit confirmation rather than making the user guess.
      if (err instanceof ApiError && err.status === 409 && message.includes('Confirm to clear')) {
        // eslint-disable-next-line no-alert
        if (window.confirm(`${message}\n\nClear the stored rows and continue?`)) {
          setBusy(false);
          return save(true);
        }
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card" style={{ marginBottom: 16, borderColor: 'var(--brand)' }}>
      <h3>{dataset ? `Edit “${dataset.name}”` : 'New dataset'}</h3>
      {error && <Banner tone="error">{error}</Banner>}

      <div className="row">
        <Field label="Name">
          <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        </Field>
        <Field label="Request to run" help="The request whose rows get stored.">
          <select value={form.request_slug} onChange={(e) => setForm({ ...form, request_slug: e.target.value })}>
            <option value="">— choose a request —</option>
            {(requests.data ?? []).map((r) => (
              <option key={r.slug} value={r.slug}>
                {r.name}
              </option>
            ))}
          </select>
        </Field>
      </div>

      <Field label="Description">
        <textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
      </Field>

      <div className="row">
        <Field label="Mode" help={MODE_HELP[form.mode]}>
          <select value={form.mode} onChange={(e) => setForm({ ...form, mode: e.target.value as any })}>
            <option value="snapshot">snapshot — a dated copy every sync</option>
            <option value="append">append — keep every row ever seen</option>
            <option value="upsert">upsert — one current row per key</option>
          </select>
        </Field>
        <Field
          label="Key columns"
          help={
            form.mode === 'snapshot'
              ? 'Optional in snapshot mode.'
              : 'Required. Comma separated. These identify the same row between syncs — without them every sync would duplicate the data.'
          }
        >
          <input
            value={keyColumns.join(', ')}
            placeholder="consultant, week_ending"
            onChange={(e) => setKeyColumns(e.target.value.split(',').map((s) => s.trim()).filter(Boolean))}
          />
        </Field>
      </div>

      <div className="row">
        <Field label="Sync schedule (cron)" help="Blank means manual only.">
          <input className="mono" value={form.cron ?? ''} onChange={(e) => setForm({ ...form, cron: e.target.value })} />
        </Field>
        <Field label="Timezone">
          <input value={form.timezone} onChange={(e) => setForm({ ...form, timezone: e.target.value })} />
        </Field>
        <Field label="Keep rows for (days)" help="Blank keeps everything. Only prune history you are certain no report will want.">
          <input type="number" value={form.retention_days} onChange={(e) => setForm({ ...form, retention_days: e.target.value })} />
        </Field>
      </div>

      <JsonEditor
        label="Fixed parameters for the sync"
        help='Passed to the request on every sync, e.g. {"weeks": 2}. Widen this temporarily to backfill history, then set it back.'
        value={params}
        rows={4}
        onChange={(parsed, ok) => {
          setValid(ok);
          if (ok) setParams(parsed ?? {});
        }}
      />

      <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13, marginBottom: 14 }}>
        <input type="checkbox" checked={form.enabled} onChange={(e) => setForm({ ...form, enabled: e.target.checked })} />
        Sync on schedule
      </label>

      <div style={{ display: 'flex', gap: 8 }}>
        <button className="primary" onClick={() => save()} disabled={busy}>
          {busy ? 'Saving…' : 'Save'}
        </button>
        <button onClick={onClose} disabled={busy}>
          Cancel
        </button>
      </div>
    </div>
  );
}
